import { useState } from 'react';
import toast from 'react-hot-toast';
import { contractsApi } from '../services/api';
import type { FamilyLoanTerms } from '../types';

/** Raise a family loan: a private agreement between two individuals.
 *
 *  The form takes amounts in major units and the rate in percent, because
 *  that is how people say them, and converts once on submit: money to MINOR
 *  units, the rate to basis points. The server computes the schedule from
 *  those integers, so nothing that decides what is owed passes through a
 *  float in the browser. */

const FIELD = 'w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900';

const toMinor = (value: string) => Math.round(parseFloat(value.replace(/,/g, '')) * 100);
const toBps = (value: string) => Math.round(parseFloat(value) * 100);

export default function FamilyLoanForm({ onRaised, onCancel }: {
  onRaised: () => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState({
    lenderName: '', lenderAddress: '', borrowerName: '', borrowerAddress: '',
    relationship: '', currency: 'USD', principal: '', rate: '1', instalments: '60',
    initialMonths: '12', advanceDate: '',
    advanceCurrency: 'EUR', setRate: '', setRateDate: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof f) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setF((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const terms: FamilyLoanTerms = {
      lenderName: f.lenderName.trim(),
      lenderAddress: f.lenderAddress.trim(),
      borrowerName: f.borrowerName.trim(),
      borrowerAddress: f.borrowerAddress.trim(),
      relationship: f.relationship.trim(),
      currency: f.currency,
      principalMinor: toMinor(f.principal),
      rateBps: toBps(f.rate),
      instalments: parseInt(f.instalments, 10),
      initialMonths: parseInt(f.initialMonths, 10),
      advanceDate: f.advanceDate || undefined,
    };
    if (f.advanceCurrency !== f.currency) {
      terms.advanceCurrency = f.advanceCurrency;
      terms.setRate = f.setRate.trim();
      terms.setRateDate = f.setRateDate;
    }
    if (!Number.isFinite(terms.principalMinor) || terms.principalMinor <= 0) {
      toast.error('Enter the amount lent.');
      return;
    }
    setBusy(true);
    try {
      const item = await contractsApi.generateFamilyLoan(terms);
      toast.success(`Raised ${item.key || 'the loan agreement'}`);
      onRaised();
    } catch (err: any) {
      // The server's reason (no published template, impossible terms) is the
      // useful part; a generic failure would throw it away.
      toast.error(err?.message || 'Could not raise the loan agreement');
    } finally {
      setBusy(false);
    }
  };

  const label = (text: string, hint?: string) => (
    <span className="block text-[11px] text-gray-600 dark:text-gray-400 mb-0.5">
      {text}{hint && <span className="text-gray-400"> · {hint}</span>}
    </span>
  );

  return (
    <form onSubmit={(e) => void submit(e)} className="p-3 space-y-3 border-t border-gray-200 dark:border-gray-700">
      <div className="grid grid-cols-2 gap-3">
        <label>{label('Lender')}<input required className={FIELD} value={f.lenderName} onChange={set('lenderName')} /></label>
        <label>{label('Borrower')}<input required className={FIELD} value={f.borrowerName} onChange={set('borrowerName')} /></label>
        <label>{label('Lender address')}<input className={FIELD} value={f.lenderAddress} onChange={set('lenderAddress')} /></label>
        <label>{label('Borrower address')}<input className={FIELD} value={f.borrowerAddress} onChange={set('borrowerAddress')} /></label>
        <label>{label('Lender is the borrower’s', 'e.g. sister-in-law')}<input className={FIELD} value={f.relationship} onChange={set('relationship')} /></label>
        <label>{label('Currency')}
          <select className={FIELD} value={f.currency} onChange={set('currency')}>
            <option>USD</option><option>EUR</option><option>GBP</option>
          </select>
        </label>
        <label>{label('Amount lent')}<input required inputMode="decimal" className={FIELD} value={f.principal} onChange={set('principal')} placeholder="60000" /></label>
        <label>{label('Interest rate %', 'from the advance date')}<input required inputMode="decimal" className={FIELD} value={f.rate} onChange={set('rate')} /></label>
        <label>{label('Interest-only months', 'their interest paid as one lump sum')}<input required inputMode="numeric" className={FIELD} value={f.initialMonths} onChange={set('initialMonths')} /></label>
        <label>{label('Monthly instalments')}<input required inputMode="numeric" className={FIELD} value={f.instalments} onChange={set('instalments')} /></label>
        <label>{label('Transfer date', 'optional; usually recorded at transfer')}<input type="date" className={FIELD} value={f.advanceDate} onChange={set('advanceDate')} /></label>
        <label>{label('Money sent in')}
          <select className={FIELD} value={f.advanceCurrency} onChange={set('advanceCurrency')}>
            <option>EUR</option><option>USD</option><option>GBP</option>
          </select>
        </label>
        <label>{label(`Set rate: ${f.advanceCurrency} 1 = ${f.currency} …`, 'fixed for the life of the loan')}<input inputMode="decimal" className={FIELD} value={f.setRate} onChange={set('setRate')} placeholder="1.1188" disabled={f.advanceCurrency === f.currency} /></label>
        <label>{label('Rate published on', 'e.g. the ECB reference date')}<input type="date" className={FIELD} value={f.setRateDate} onChange={set('setRateDate')} disabled={f.advanceCurrency === f.currency} /></label>
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        The repayment schedule is computed on raise (the initial period's interest as one
        lump sum, then equal monthly instalments) and appended as Annex A, counted in
        months from the day the money arrives. The agreement is dated by its last
        signature. Anything left blank stays a visible
        placeholder in the draft rather than a guess.
      </p>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md">Cancel</button>
        <button type="submit" disabled={busy} className="px-3 py-1 text-xs rounded-md bg-indigo-600 text-white disabled:opacity-50">
          {busy ? 'Raising…' : 'Raise loan agreement'}
        </button>
      </div>
    </form>
  );
}
